import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  OAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  browserLocalPersistence,
  sendEmailVerification as _sendEmailVerification,
  User,
  Auth,
} from "firebase/auth";

// Firebase client configuration
const FIREBASE_CLIENT_CONFIG = {
  apiKey: "AIzaSyBvms4LVcVsdtk9JOuU1gudHcu489yRLZ4",
  authDomain: "lyfsho.firebaseapp.com",
  databaseURL: "https://lyfsho.firebaseio.com",
  projectId: "lyfsho",
  storageBucket: "lyfsho.appspot.com",
  messagingSenderId: "757943937010",
  appId: "1:757943937010:web:09809a6c25c2e9d319ed6b",
  measurementId: "G-XPXY96MXGP",
};

// Initialize Firebase app
const firebaseApp: FirebaseApp = initializeApp(FIREBASE_CLIENT_CONFIG);
const auth: Auth = getAuth(firebaseApp);
auth.setPersistence(browserLocalPersistence);

let signinMethod: "email" | "google" | "apple" | null = null;

let signupData: object | null = null;

/**
 * Sign in using email and password.
 */
export async function signIn(email: string, password: string): Promise<User | null> {
  if (email.length < 4) {
    alert("Please enter an email address.");
    return null;
  }
  if (password.length < 4) {
    alert("Please enter a password.");
    return null;
  }
  signinMethod = "email";

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: unknown) {
    if (typeof error === "object" && error !== null && "code" in error) {
      const errorCode = (error as { code: string }).code;
      const errorMessage = (error as object as { message: string }).message;
      if (errorCode === "auth/wrong-password") {
        alert("Wrong password.");
      } else {
        alert(errorMessage);
      }
    }
    console.error(error);
    return null;
  }
}

/**
 * Sign in using Google OAuth.
 */
export async function googleSignIn(): Promise<User | null> {
  signinMethod = "google";
  const provider = new GoogleAuthProvider();

  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: unknown) {
    console.error(error);
    return null;
  }
}

/**
 * Sign in using Apple OAuth.
 */
export async function appleSignIn(): Promise<User | null> {
  signinMethod = "apple";
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");

  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: unknown) {
    console.error(error);
    return null;
  }
}

/**
 * Return a promise that resolves to the current user
 * 
 * Use this instead of Auth.currentUser to avoid problems with user==null
 * while firebase is initializing
 */

export async function getCurrentUser(): Promise<User | null> {
  return new Promise((resolve, reject) => {
    const unsubscribe = getAuth(firebaseApp).onIdTokenChanged(
      (user) => {
        resolve(user)
        setTimeout(() => {
          unsubscribe();
        }, 100);
      },
      (error) => {
        reject(error)
        setTimeout(() => {
          unsubscribe();
        }, 100);
      }
    )
  })
}

/**
 * Sign out the current user.
 */
export async function signOut(): Promise<void> {
  signinMethod = null;
  if (auth.currentUser) {
    await auth.signOut();
  }
}

/**
 * Sign up a new user with email, password, and display name.
 */
export async function signUp(email: string, password: string, name: string, extraData: object): Promise<User | null> {
  signupData = extraData

  try {
    const newUser = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(newUser.user, { displayName: name });
    return newUser.user;
  } catch (error: unknown) {
    console.error("Error signing up:", error);

    if (typeof error === "object" && error !== null && "code" in error) {
      const errorCode = (error as { code: string }).code;
      if (errorCode !== "auth/email-already-in-use" && errorCode !== "auth/weak-password") {
        // Capture error in Sentry or another logging system
        // Sentry.captureException(error);
      }
    }

    return null;
  }
}

/**
 * Send a password reset email.
 */
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Send an email verification to the current user.
 */
export async function sendEmailVerification(): Promise<void> {
  const user = auth.currentUser;
  if (user) {
    console.log("Sending verification email...");
    await _sendEmailVerification(user);
  }
}

/**
 * Get the Firebase app instance.
 */
export function getFirebaseApp(): FirebaseApp {
  return firebaseApp;
}

/**
 * Get the last used sign-in method.
 */
export function getSignInMethod(): string | null {
  return signinMethod;
}

export function getSignupData(): object | null {
  return signupData;
}

export function clearSignupData(): void {
  signupData = null
}